import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, X, ArrowDownWideNarrow } from "lucide-react";
import api from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import TopicCard, { evidenceCount } from "../components/TopicCard";
import ArchiveScaleCounter from "../components/ArchiveScaleCounter";
import { GeoChipRow, IntensityChipRow } from "../components/TagFilters";
import SEO, { websiteJsonLd } from "../components/SEO";

const HERO_IMG = "https://images.unsplash.com/photo-1773578132706-39205095dfb1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjByZXNlYXJjaCUyMGxpYnJhcnklMjBpbnRlcmlvcnxlbnwwfHx8fDE3Nzc0MDI4ODV8MA&ixlib=rb-4.1.0&q=85";

const SORTS = [
    { key: "featured", label: "Featured" },
    { key: "most-connected", label: "Most Connected" },
    { key: "highest-confidence", label: "Highest Confidence" },
    { key: "highest-concern", label: "Highest Concern" },
    { key: "recently-updated", label: "Recently Updated" },
    { key: "highest-evidence-density", label: "Highest Evidence Density" },
    { key: "alphabetical", label: "A → Z" },
];

function applySort(items, key) {
    const arr = [...items];
    const num = (v) => (typeof v === "number" ? v : 0);
    switch (key) {
        case "most-connected":
            return arr.sort((a, b) => (b.related_topics?.length || 0) - (a.related_topics?.length || 0));
        case "highest-confidence":
            return arr.sort((a, b) => num(b.confidence_score) - num(a.confidence_score));
        case "highest-concern":
            return arr.sort((a, b) => num(b.concern_level) - num(a.concern_level));
        case "recently-updated":
            return arr.sort((a, b) => (new Date(b.last_updated || 0)) - (new Date(a.last_updated || 0)));
        case "highest-evidence-density":
            return arr.sort((a, b) => evidenceCount(b) - evidenceCount(a));
        case "alphabetical":
            return arr.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
        case "featured":
        default:
            return arr.sort((a, b) => num(b.priority) - num(a.priority));
    }
}

export default function LibraryPage() {
    const [topics, setTopics] = useState([]);
    const [categories, setCategories] = useState([]);
    const [filters, setFilters] = useState({ geographies: [], intensities: [], categories: [] });
    const [activeCategory, setActiveCategory] = useState("All");
    const [geo, setGeo] = useState("");
    const [intensity, setIntensity] = useState("");
    const [q, setQ] = useState("");
    const [sortKey, setSortKey] = useState("featured");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            api.get("/topics?status=published"),
            api.get("/categories"),
            api.get("/filters"),
        ])
            .then(([t, c, f]) => {
                setTopics(t.data || []);
                setCategories(c.data || []);
                setFilters(f.data || { geographies: [], intensities: [], categories: [] });
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        let items = topics;
        if (activeCategory !== "All") {
            items = items.filter((t) => t.category === activeCategory);
        }
        if (geo) items = items.filter((t) => (t.geography || []).includes(geo));
        if (intensity) items = items.filter((t) => (t.intensity || "Documented") === intensity);
        if (q.trim()) {
            const ql = q.trim().toLowerCase();
            items = items.filter((t) => {
                const hay = [
                    t.title, t.subtitle, t.summary, t.overview, t.category,
                    ...(t.tags || []),
                ].filter(Boolean).join(" ").toLowerCase();
                return hay.includes(ql);
            });
        }
        return applySort(items, sortKey);
    }, [topics, activeCategory, geo, intensity, q, sortKey]);

    const activeSortLabel = SORTS.find((s) => s.key === sortKey)?.label || "Featured";

    return (
        <div className="min-h-screen bg-[#0B1220]">
            <SEO
                title="Library"
                description="Structured, evidence-led breakdowns of complex, controversial, and misunderstood topics. Every claim cited. Both sides represented. Confidence and concern made visible."
                canonicalPath="/"
                jsonLd={websiteJsonLd()}
            />
            <Header />

            {/* Hero */}
            <section className="relative overflow-hidden border-b border-[#243044]">
                <div
                    className="absolute inset-0 opacity-[0.08] bg-cover bg-center"
                    style={{ backgroundImage: `url(${HERO_IMG})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0B1220]/40 via-[#0B1220]/85 to-[#0B1220]" />
                <div className="absolute inset-0 bg-grid opacity-30" />

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 sm:pt-32 sm:pb-28">
                    <div className="max-w-3xl fade-in">
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-white mb-6">
                            Context for the questions
                            <br />
                            <span className="text-[#56CCF2]">that actually matter.</span>
                        </h1>

                        <p className="text-lg sm:text-xl text-[#94A3B8] leading-relaxed max-w-2xl mb-10">
                            The Context Project is an evidence-based research library providing structured, neutral breakdowns of complex, controversial and misunderstood topics. Every claim is cited. Both sides are represented. Confidence and concern are made visible. Independently researched and compiled by Bill Mathers.
                        </p>
                    </div>

                    {/* Wave 6 — visible archive scale */}
                    <div className="mt-12 max-w-5xl">
                        <ArchiveScaleCounter />
                    </div>
                </div>
            </section>

            {/* Discovery bar */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
                <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                    <h2 className="text-2xl font-bold text-white">Browse the library</h2>
                    <div className="text-xs text-[#64748B] uppercase tracking-wider">
                        Sorted by <span className="text-[#56CCF2]">{activeSortLabel}</span>
                    </div>
                </div>

                <div className="bg-[#111827] border border-[#243044] rounded-2xl p-4 sm:p-5 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
                        <div className="flex items-center gap-3 bg-[#0B1220] border border-[#243044] rounded-xl px-4 py-3 focus-within:border-[#2F80ED]">
                            <Search className="w-4 h-4 text-[#64748B] shrink-0" />
                            <input
                                data-testid="library-search-input"
                                type="text"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search the library — topic, tag, organisation, person…"
                                className="bg-transparent border-0 outline-none text-sm text-white placeholder-[#64748B] flex-1"
                            />
                            {q && (
                                <button
                                    data-testid="library-search-clear"
                                    onClick={() => setQ("")}
                                    className="text-[#64748B] hover:text-white"
                                    aria-label="Clear search"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>

                        <div className="flex items-center gap-3 bg-[#0B1220] border border-[#243044] rounded-xl px-4 py-3 focus-within:border-[#2F80ED]">
                            <ArrowDownWideNarrow className="w-4 h-4 text-[#64748B] shrink-0" />
                            <select
                                data-testid="library-sort"
                                value={sortKey}
                                onChange={(e) => setSortKey(e.target.value)}
                                className="bg-transparent border-0 outline-none text-sm text-white pr-2 min-w-[180px] cursor-pointer"
                            >
                                {SORTS.map((s) => (
                                    <option key={s.key} value={s.key}>{s.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-2 pt-1">
                        <button
                            data-testid="category-filter-all"
                            onClick={() => setActiveCategory("All")}
                            className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider font-medium transition-colors border ${activeCategory === "All" ? "bg-[#2F80ED] text-white border-[#2F80ED]" : "bg-[#0B1220] text-[#94A3B8] border-[#243044] hover:border-[#2F80ED]"}`}
                        >
                            All
                        </button>
                        {categories.map((c) => (
                            <button
                                key={c}
                                data-testid={`category-filter-${c}`}
                                onClick={() => setActiveCategory(c)}
                                className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider font-medium transition-colors border ${activeCategory === c ? "bg-[#2F80ED] text-white border-[#2F80ED]" : "bg-[#0B1220] text-[#94A3B8] border-[#243044] hover:border-[#2F80ED]"}`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>

                    {(filters.geographies.length > 0 || filters.intensities.length > 0) && (
                        <div className="pt-2 space-y-3 border-t border-[#243044]/60">
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-2">Region</p>
                                <GeoChipRow
                                    value={geo}
                                    options={filters.geographies}
                                    onChange={setGeo}
                                    dataTestId="library-geo"
                                />
                            </div>
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-2">Intensity</p>
                                <IntensityChipRow
                                    value={intensity}
                                    options={filters.intensities}
                                    onChange={setIntensity}
                                    dataTestId="library-intensity"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* Topic grid */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="flex items-baseline justify-between mb-6">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] font-medium">
                        {activeCategory === "All" ? "All categories" : activeCategory}
                    </div>
                    <div data-testid="library-result-count" className="text-xs text-[#64748B]">
                        {loading ? "Loading…" : `${filtered.length} topic${filtered.length === 1 ? "" : "s"}`}
                    </div>
                </div>

                {loading ? (
                    <div className="text-center text-sm text-[#64748B] py-20">Loading library…</div>
                ) : filtered.length === 0 ? (
                    <div className="bg-[#111827] border border-[#243044] rounded-2xl p-12 text-center">
                        <h3 className="text-xl font-semibold text-white mb-2">No topics match your filters</h3>
                        <p className="text-sm text-[#94A3B8] mb-6 max-w-md mx-auto">
                            {topics.length === 0
                                ? "The library is being prepared. Topics will appear here once published."
                                : "Try a different search term or clear the active category filter."}
                        </p>
                        {topics.length === 0 ? (
                            <Link
                                to="/admin/login"
                                className="inline-flex items-center text-sm text-[#56CCF2] hover:text-white transition-colors"
                                data-testid="empty-state-admin-link"
                            >
                                Admin sign in →
                            </Link>
                        ) : (
                            <button
                                onClick={() => { setQ(""); setActiveCategory("All"); }}
                                className="inline-flex items-center text-sm text-[#56CCF2] hover:text-white transition-colors"
                            >
                                Clear filters →
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="topic-grid">
                        {filtered.map((t) => (
                            <TopicCard key={t.id || t.slug} topic={t} />
                        ))}
                    </div>
                )}
            </section>

            <Footer />
        </div>
    );
}
